import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  flexRender,
  functionalUpdate,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Button from "./Button";
import { Checkbox } from "./Inputs";
import MenuItem from "./MenuItem";
import Modal from "./Modal";

const DefaultUI = {
  Button: (props) => (
    <Button
      variant={props.variant || "ghost"}
      size={props.size || "sm"}
      icon={"vert more"}
      {...props}
    />
  ),
  Link: (props) => <a {...props} />,
  MenuItem: ({ children, ...rest }) => (
    <MenuItem css="rm-tooltip__item row" {...rest} />
  ),
};

const DEFAULT_COLUMNS = [
  { key: "name", label: "Name", type: "text", draggable: true },
  { key: "site", label: "Website", type: "url", draggable: true },
  { key: "score", label: "Score", type: "formula", draggable: true },
];

const DEFAULT_ROWS = [
  {
    name: "Alpha",
    site: "https://alpha.example.com",
    a: 10,
    b: 5,
    score: "=a + b * 2",
  },
  {
    name: "Beta",
    site: "https://beta.example.com",
    a: 3,
    b: 7,
    score: "=(a + b) / 2",
  },
  {
    name: "Gamma",
    site: "https://gamma.example.com",
    a: 12,
    b: 2,
    score: "=a - b",
  },
];

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function normalizeColumn(col) {
  return {
    hidden: false,
    type: "text",
    draggable: true,
    enableSorting: true,
    ...col,
    label: col?.label ?? col?.title ?? col?.key ?? "Column",
    key: col?.key ?? col?.id ?? `col_${Date.now()}`,
  };
}

function buildVisibility(columns) {
  return Object.fromEntries(columns.map((col) => [col.key, !col.hidden]));
}

function buildColumnOrder(columns) {
  return columns.map((col) => col.key);
}

function buildColumnSizing(columns) {
  return Object.fromEntries(
    columns
      .filter((col) => Number.isFinite(col.width) && col.width > 0)
      .map((col) => [col.key, col.width]),
  );
}

function getInitialColumns(columns) {
  const source = columns?.length ? columns : DEFAULT_COLUMNS;
  return source.map(normalizeColumn);
}

function getInitialRows(rows) {
  return rows?.length ? rows : DEFAULT_ROWS;
}

function evalFormula(raw, row) {
  if (typeof raw !== "string" || raw.trim()[0] !== "=") return raw;
  const expr = raw.trim().slice(1);
  const sanitized = expr.replace(/\b([a-zA-Z_][a-zA-Z0-9_]*)\b/g, (match) => {
    if (typeof Math[match] !== "undefined") return `Math.${match}`;
    const value = row[match];
    const num = typeof value === "number" ? value : parseFloat(value);
    return Number.isFinite(num) ? String(num) : "0";
  });
  try {
    // eslint-disable-next-line no-new-func
    const result = Function(`"use strict"; return (${sanitized});`)();
    return Number.isFinite(result) ? result : "";
  } catch {
    return "";
  }
}

const CellView = React.forwardRef(function CellView(
  {
    id,
    roleType = "td",
    className,
    style,
    draggable,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    children,
    ...rest
  },
  ref,
) {
  const base = roleType === "th" ? "rm-table__th" : "rm-table__td";
  return (
    <div
      id={id}
      data-cell={roleType}
      className={[base, "grid__col", className].filter(Boolean).join(" ")}
      style={style}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      ref={ref}
      {...rest}
    >
      {children}
    </div>
  );
});

function Cell({ type, value, row, UI, column }) {
  if (typeof column?.cell === "function") {
    return column.cell({ value, row, column });
  }

  switch (type) {
    case "url": {
      const href = String(value || "");
      const label = href.replace(/^https?:\/\//, "");
      const LinkComp = UI?.Link || DefaultUI.Link;
      return (
        <LinkComp
          className="rm-table__link"
          href={href}
          target="_blank"
          rel="noreferrer"
        >
          {label}
        </LinkComp>
      );
    }
    case "formula": {
      const out = evalFormula(value, row);
      return <span className="rm-table__formula">{String(out)}</span>;
    }
    default:
      return <span className="rm-table__text">{String(value ?? "")}</span>;
  }
}

function HeaderMenu({
  col,
  open,
  onToggle,
  onHide,
  onAddLeft,
  onAddRight,
  onDelete,
  onStartEdit,
  editable = true,
  sorting,
  setSorting,
}) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        onToggle?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onToggle]);

  const sortEntry = sorting.find((entry) => entry.id === col.key);
  const sortDir = sortEntry ? (sortEntry.desc ? "desc" : "asc") : null;

  return (
    <div
      className="rm-table__actions_container grid"
      tabIndex={0}
      ref={containerRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      <Button
        className={["rm-table__menu-btn", open ? "open" : ""]
          .filter(Boolean)
          .join(" ")}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={onToggle}
        size="sm"
        icon="more_vert"
        iconOnly
      >
        <div
          className="btn-extra-content"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          {open && (
            <div
              className="rm-table__menu-dropdown"
              role="menu"
              style={{ pointerEvents: "auto" }}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
            >
              {col.enableSorting !== false && (
                <>
                  <div className="rm-table__menu-section">Sort</div>
                  <MenuItem
                    className={[
                      "rm-tooltip__item",
                      sortDir === "asc" ? "selected" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    role="menuitem"
                    icon="arrow_upward"
                    onClick={(event) => {
                      event.preventDefault();
                      setSorting([{ id: col.key, desc: false }]);
                      onToggle?.();
                    }}
                  >
                    Ascending
                  </MenuItem>
                  <MenuItem
                    className={[
                      "rm-tooltip__item",
                      sortDir === "desc" ? "selected" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    role="menuitem"
                    icon="arrow_downward"
                    onClick={(event) => {
                      event.preventDefault();
                      setSorting([{ id: col.key, desc: true }]);
                      onToggle?.();
                    }}
                  >
                    Descending
                  </MenuItem>
                </>
              )}

              <div className="rm-table__menu-section">Actions</div>
              <MenuItem
                icon="visibility_off"
                role="menuitem"
                className="rm-tooltip__item"
                onMouseDown={(event) => {
                  event.preventDefault();
                  onHide?.();
                  onToggle?.();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  onHide?.();
                  onToggle?.();
                }}
              >
                Hide Column
              </MenuItem>

              {editable && (
                <MenuItem
                  icon="edit"
                  role="menuitem"
                  className="rm-tooltip__item"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    onStartEdit?.();
                    onToggle?.();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    onStartEdit?.();
                    onToggle?.();
                  }}
                >
                  Edit Column
                </MenuItem>
              )}

              {editable && (
                <MenuItem
                  icon="delete"
                  role="menuitem"
                  className="rm-tooltip__item"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    onDelete?.();
                    onToggle?.();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    onDelete?.();
                    onToggle?.();
                  }}
                >
                  Delete Column
                </MenuItem>
              )}

              <MenuItem
                icon="arrow_left"
                role="menuitem"
                className="rm-tooltip__item"
                onMouseDown={(event) => {
                  event.preventDefault();
                  onAddLeft?.();
                  onToggle?.();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  onAddLeft?.();
                  onToggle?.();
                }}
              >
                Add to left
              </MenuItem>

              <MenuItem
                icon="arrow_right"
                role="menuitem"
                className="rm-tooltip__item"
                onMouseDown={(event) => {
                  event.preventDefault();
                  onAddRight?.();
                  onToggle?.();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  onAddRight?.();
                  onToggle?.();
                }}
              >
                Add to right
              </MenuItem>
            </div>
          )}
        </div>
      </Button>
    </div>
  );
}

export default function DataTable({
  tableTitle: title,
  tableCaption: caption,
  columns: inputCols,
  rows: inputRows,
  data,
  onChange,
  onRowsChange,
  ui,
  className = "",
  getRowClassName,
  renderRowActions,
  rowActionsLabel = "Actions",
  editable = true,
  stackToCards = true,
  stickyHeader = false,
}) {
  const UI = { ...DefaultUI, ...(ui || {}) };
  const tableRef = useRef(null);
  const thRefs = useRef({});
  const dragColKey = useRef(null);

  const resolvedInputRows = inputRows ?? data;
  const [cols, setCols] = useState(() => getInitialColumns(inputCols));
  const [rows, setRows] = useState(() => getInitialRows(resolvedInputRows));
  const [sorting, setSorting] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildVisibility(getInitialColumns(inputCols)),
  );
  const [columnOrder, setColumnOrder] = useState(() =>
    buildColumnOrder(getInitialColumns(inputCols)),
  );
  const [columnSizing, setColumnSizing] = useState(() =>
    buildColumnSizing(getInitialColumns(inputCols)),
  );
  const [openCols, setOpenCols] = useState({});
  const [openTitleMenu, setOpenTitleMenu] = useState(false);
  const [isDraggingCol, setIsDraggingCol] = useState(false);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [editing, setEditing] = useState(null);
  const [cellEdit, setCellEdit] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const nextCols = getInitialColumns(inputCols);
    setCols(nextCols);
    setColumnVisibility(buildVisibility(nextCols));
    setColumnOrder(buildColumnOrder(nextCols));
    setColumnSizing(buildColumnSizing(nextCols));
  }, [inputCols]);

  useEffect(() => {
    setRows(getInitialRows(resolvedInputRows));
  }, [resolvedInputRows]);

  useEffect(() => {
    const mq =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(!!mq && !!mq.matches);
    update();
    mq?.addEventListener
      ? mq.addEventListener("change", update)
      : mq?.addListener?.(update);
    return () => {
      mq?.removeEventListener
        ? mq.removeEventListener("change", update)
        : mq?.removeListener?.(update);
    };
  }, []);

  useEffect(() => {
    if (!tableRef.current) return;
    requestAnimationFrame(() => {
      const scope = tableRef.current;
      const cells = scope.querySelectorAll(".rm-table__td, .rm-table__th");
      let maxWidth = 0;
      cells.forEach((cell) => {
        const width = cell.offsetWidth || 0;
        if (width > maxWidth) maxWidth = width;
      });
      if (maxWidth > 0) {
        scope.style.setProperty("--table-max-cell", `${maxWidth}px`);
      }
    });
  }, [rows, cols, columnSizing, sorting]);

  const commitColumns = (nextCols) => {
    setCols(nextCols);
    setColumnVisibility(buildVisibility(nextCols));
    setColumnOrder(buildColumnOrder(nextCols));
    setColumnSizing(buildColumnSizing(nextCols));
    onChange?.(nextCols);
  };

  const updateColumnSizing = (updater) => {
    setColumnSizing((prev) => {
      const next = functionalUpdate(updater, prev);
      setCols((current) => {
        const nextCols = current.map((col) =>
          Number.isFinite(next[col.key]) && next[col.key] > 0
            ? { ...col, width: Math.round(next[col.key]) }
            : col,
        );
        onChange?.(nextCols);
        return nextCols;
      });
      return next;
    });
  };

  const tanstackColumns = useMemo(() => {
    const defs = [];

    if (editable) {
      defs.push({
        id: "__select",
        size: 64,
        minSize: 64,
        maxSize: 64,
        enableSorting: false,
        enableResizing: false,
        enableHiding: false,
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={
              table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
            }
            onChange={(event) =>
              table.toggleAllRowsSelected(!!event.target?.checked)
            }
            label=""
            size="sm"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onChange={(event) => row.toggleSelected(!!event.target?.checked)}
            label=""
            size="sm"
          />
        ),
      });
    }

    if (typeof renderRowActions === "function") {
      defs.push({
        id: "__actions",
        size: 72,
        minSize: 72,
        enableSorting: false,
        enableResizing: false,
        enableHiding: false,
        header: () => rowActionsLabel,
        cell: ({ row }) => (
          <div className="rm-table__row-actions">
            {renderRowActions(row.original)}
          </div>
        ),
      });
    }

    cols.forEach((col) => {
      defs.push({
        id: col.key,
        accessorFn: (row) => row[col.key],
        enableSorting: col.enableSorting !== false,
        enableHiding: true,
        enableResizing: col.resizable !== false,
        size: Number.isFinite(col.width) ? col.width : undefined,
        meta: { col },
        sortingFn: (rowA, rowB, columnId) => {
          const leftRow = rowA.original;
          const rightRow = rowB.original;
          let left = leftRow[columnId];
          let right = rightRow[columnId];
          if (col.type === "formula") {
            left = evalFormula(left, leftRow);
            right = evalFormula(right, rightRow);
          }
          const asComparable = (value) => {
            if (typeof value === "number") return value;
            if (typeof value === "boolean") return value ? 1 : 0;
            if (value == null) return "";
            const str = String(value).trim();
            const num = Number(str);
            if (str !== "" && !Number.isNaN(num) && String(num) === str) {
              return num;
            }
            return str.toLowerCase();
          };
          const a = asComparable(left);
          const b = asComparable(right);
          if (typeof a === "number" && typeof b === "number") return a - b;
          return String(a).localeCompare(String(b));
        },
        header: () => col.label,
        cell: ({ row }) => {
          const value = row.original[col.key];

          if (
            editable &&
            cellEdit &&
            cellEdit.rowIndex === row.index &&
            cellEdit.key === col.key
          ) {
            return (
              <input
                className="rm-table__cell-input"
                value={cellEdit.value}
                onChange={(event) =>
                  setCellEdit((state) => ({
                    ...state,
                    value: event.target.value,
                  }))
                }
                onBlur={() => {
                  if (!cellEdit) return;
                  setRows((current) => {
                    const nextRows = current.map((entry, index) =>
                      index === cellEdit.rowIndex
                        ? { ...entry, [cellEdit.key]: cellEdit.value }
                        : entry,
                    );
                    onRowsChange?.(nextRows);
                    return nextRows;
                  });
                  setCellEdit(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.currentTarget.blur();
                  }
                  if (event.key === "Escape") {
                    setCellEdit(null);
                  }
                }}
                autoFocus
              />
            );
          }

          return (
            <span
              className="cell-text"
              onDoubleClick={() =>
                editable &&
                setCellEdit({
                  rowIndex: row.index,
                  key: col.key,
                  value: value ?? "",
                })
              }
              title={String(value ?? "")}
            >
              <Cell
                UI={UI}
                type={col.type}
                value={value}
                row={row.original}
                column={col}
              />
            </span>
          );
        },
      });
    });

    return defs;
  }, [
    UI,
    cellEdit,
    cols,
    editable,
    onRowsChange,
    renderRowActions,
    rowActionsLabel,
  ]);

  const table = useReactTable({
    data: rows,
    columns: tanstackColumns,
    state: {
      sorting,
      rowSelection,
      columnVisibility,
      columnOrder:
        editable && typeof renderRowActions === "function"
          ? ["__select", ...columnOrder, "__actions"]
          : editable
            ? ["__select", ...columnOrder]
            : typeof renderRowActions === "function"
              ? [...columnOrder, "__actions"]
              : columnOrder,
      columnSizing,
    },
    enableRowSelection: editable,
    columnResizeMode: "onChange",
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onColumnSizingChange: updateColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const visibleDataColumns = table
    .getVisibleLeafColumns()
    .filter((column) => column.id !== "__select" && column.id !== "__actions");
  const totalVisibleCols = table.getVisibleLeafColumns().length;
  const gridStyle = useMemo(() => {
    const hasRowActions = typeof renderRowActions === "function";
    const dataColCount =
      totalVisibleCols - (editable ? 1 : 0) - (hasRowActions ? 1 : 0);
    const dataTracks = `repeat(${Math.max(dataColCount, 0)}, minmax(0, 1fr))`;
    let template = dataTracks;

    if (editable) {
      template = `var(--gridLead) ${dataTracks}`;
    }
    if (hasRowActions) {
      template = `${template} var(--rmActionsCol)`;
    }

    return {
      "--gridCols": String(Math.max(totalVisibleCols - 1, 0)),
      "--rmActionsCol": "72px",
      "--rmTableTemplate": template,
    };
  }, [editable, renderRowActions, totalVisibleCols]);

  function reorderColumns(fromKey, toKey) {
    if (!fromKey || !toKey || fromKey === toKey) return;
    const fromIndex = cols.findIndex((col) => col.key === fromKey);
    const toIndex = cols.findIndex((col) => col.key === toKey);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;
    const next = [...cols];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(clamp(toIndex, 0, next.length), 0, moved);
    commitColumns(next);
  }

  function handleAddColumn(atIndex) {
    const newCol = normalizeColumn({
      key: `col_${Date.now()}`,
      label: "New",
      type: "text",
    });
    const next = [...cols];
    next.splice(atIndex, 0, newCol);
    commitColumns(next);
  }

  function handleShowHiddenColumn(key) {
    const next = cols.map((col) =>
      col.key === key ? { ...col, hidden: false } : col,
    );
    commitColumns(next);
  }

  function handleConfirmDelete() {
    if (!confirmDel) return;
    const next = cols.filter((col) => col.key !== confirmDel.key);
    commitColumns(next);
    setOpenCols((prev) => {
      const copy = { ...prev };
      delete copy[confirmDel.key];
      return copy;
    });
    setConfirmDel(null);
  }

  return (
    <div
      className={["rm-table", className].filter(Boolean).join(" ")}
      role="region"
      aria-label="Data table"
      ref={tableRef}
    >
      {(title || caption) && (
        <div className="rm-table-caption rm-table__caption t-h4">
          <div className="rm-table__caption-copy">
            {title ? <span className="rm-table-title-text">{title}</span> : null}
            {caption ? (
              <span className="rm-table-caption-text">{caption}</span>
            ) : null}
          </div>

          {cols.some((col) => col.hidden) && (
            <div className="rm-thbar-actions">
              <Button
                className={[
                  "rm-table-menu-btn",
                  "rm-table__menu-btn",
                  openTitleMenu ? "open" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-haspopup="true"
                aria-expanded={openTitleMenu}
                onClick={() => setOpenTitleMenu((open) => !open)}
                size="sm"
                icon="more_vert"
                iconOnly
              >
                <div
                  className="btn-extra-content"
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => event.stopPropagation()}
                >
                  {openTitleMenu && (
                    <div
                      className="rm-table-menu-dropdown rm-table__menu-dropdown"
                      role="menu"
                      style={{ pointerEvents: "auto" }}
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div
                        className="rm-tooltip-item text-title"
                        role="presentation"
                      >
                        Hidden Columns
                      </div>
                      <MenuItem
                        icon="visibility"
                        role="menuitem"
                        className="rm-tooltip__item"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          const next = cols.map((col) => ({
                            ...col,
                            hidden: false,
                          }));
                          commitColumns(next);
                          setOpenTitleMenu(false);
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          const next = cols.map((col) => ({
                            ...col,
                            hidden: false,
                          }));
                          commitColumns(next);
                          setOpenTitleMenu(false);
                        }}
                      >
                        Show All
                      </MenuItem>
                      <hr className="rm-table-menu-divider" />
                      {cols
                        .filter((col) => col.hidden)
                        .map((col) => (
                          <MenuItem
                            key={col.key}
                            role="menuitem"
                            className="rm-tooltip-item"
                            icon="remove"
                            aria-label={`Show ${col.label}`}
                            onClick={(event) => {
                              event.preventDefault();
                              handleShowHiddenColumn(col.key);
                            }}
                          >
                            {col.label}
                          </MenuItem>
                        ))}
                    </div>
                  )}
                </div>
              </Button>
            </div>
          )}
        </div>
      )}

      <ul
        className={[
          "rm-table-list",
          "rm-table__list",
          "grid",
          stickyHeader ? "rm-table--sticky" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        role="list"
        style={gridStyle}
      >
        {!stackToCards || !isMobile
          ? table.getHeaderGroups().map((headerGroup) => (
              <li
                key={headerGroup.id}
                className={[
                  "rm-table-head",
                  "rm-table__head",
                  "row",
                  "grid",
                  "grid-lead-fixed",
                ].join(" ")}
                style={gridStyle}
              >
                {headerGroup.headers.map((header) => {
                  const isSelectCol = header.column.id === "__select";
                  const isActionsCol = header.column.id === "__actions";
                  const col = cols.find(
                    (entry) => entry.key === header.column.id,
                  );
                  return (
                    <CellView
                      key={header.id}
                      id={`cell-header-${header.column.id}`}
                      roleType="th"
                      className={[
                        isSelectCol ? "rm-thbar-lead" : "rm-thbar",
                        isActionsCol ? "rm-thbar-actions-col" : "",
                        openCols[header.column.id] ? "open" : "",
                        isDraggingCol && dragColKey.current === header.column.id
                          ? "rm-col--dragging rm-col--fading"
                          : "",
                        isDraggingCol &&
                        dragOverKey === header.column.id &&
                        dragColKey.current !== header.column.id
                          ? "rm-col--placeholder"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      draggable={
                        !isSelectCol &&
                        !isActionsCol &&
                        col?.draggable !== false
                      }
                      onDragStart={(event) => {
                        if (
                          isSelectCol ||
                          isActionsCol ||
                          col?.draggable === false
                        )
                          return;
                        dragColKey.current = header.column.id;
                        setIsDraggingCol(true);
                        setDragOverKey(header.column.id);
                      }}
                      onDragOver={(event) => {
                        if (
                          isSelectCol ||
                          isActionsCol ||
                          col?.draggable === false
                        )
                          return;
                        event.preventDefault();
                        if (isDraggingCol && dragOverKey !== header.column.id) {
                          setDragOverKey(header.column.id);
                        }
                      }}
                      onDrop={() => {
                        if (
                          isSelectCol ||
                          isActionsCol ||
                          col?.draggable === false
                        )
                          return;
                        reorderColumns(dragColKey.current, header.column.id);
                        dragColKey.current = null;
                        setIsDraggingCol(false);
                        setDragOverKey(null);
                      }}
                      onDragEnd={() => {
                        dragColKey.current = null;
                        setIsDraggingCol(false);
                        setDragOverKey(null);
                      }}
                      ref={(el) => {
                        if (!isSelectCol && !isActionsCol) {
                          thRefs.current[header.column.id] = el;
                        }
                      }}
                    >
                      {isSelectCol || isActionsCol ? (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      ) : editing?.key === col?.key ? (
                        <>
                          <input
                            className="rm-table__edit-input"
                            value={editing.value}
                            onChange={(event) =>
                              setEditing({
                                key: col.key,
                                value: event.target.value,
                              })
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                const next = cols.map((entry) =>
                                  entry.key === col.key
                                    ? { ...entry, label: editing.value }
                                    : entry,
                                );
                                commitColumns(next);
                                setEditing(null);
                              } else if (event.key === "Escape") {
                                setEditing(null);
                              }
                            }}
                            autoFocus
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            icon="check"
                            aria-label="Save label"
                            onClick={() => {
                              const next = cols.map((entry) =>
                                entry.key === col.key
                                  ? { ...entry, label: editing.value }
                                  : entry,
                              );
                              commitColumns(next);
                              setEditing(null);
                            }}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            icon="close"
                            aria-label="Cancel edit"
                            onClick={() => setEditing(null)}
                          />
                        </>
                      ) : (
                        <>
                          <span className="rm-thbar__label" title={col?.label}>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </span>
                          <div className="rm-thbar__actions">
                            <HeaderMenu
                              col={col}
                              open={!!openCols[col?.key]}
                              onToggle={() =>
                                setOpenCols((prev) => ({
                                  ...prev,
                                  [col.key]: !prev[col.key],
                                }))
                              }
                              onHide={() => {
                                const next = cols.map((entry) =>
                                  entry.key === col.key
                                    ? { ...entry, hidden: true }
                                    : entry,
                                );
                                commitColumns(next);
                              }}
                              onAddLeft={() =>
                                handleAddColumn(
                                  Math.max(
                                    cols.findIndex(
                                      (entry) => entry.key === col.key,
                                    ),
                                    0,
                                  ),
                                )
                              }
                              onAddRight={() =>
                                handleAddColumn(
                                  cols.findIndex(
                                    (entry) => entry.key === col.key,
                                  ) + 1,
                                )
                              }
                              onStartEdit={() =>
                                setEditing({ key: col.key, value: col.label })
                              }
                              onDelete={() =>
                                setConfirmDel({
                                  key: col.key,
                                  label: col.label,
                                })
                              }
                              editable={editable}
                              sorting={sorting}
                              setSorting={setSorting}
                            />
                          </div>
                          {header.column.getCanResize() && (
                            <span
                              className="rm-thbar__resizer"
                              role="separator"
                              aria-orientation="vertical"
                              aria-label={`Resize column ${col.label}`}
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                            />
                          )}
                        </>
                      )}
                    </CellView>
                  );
                })}
              </li>
            ))
          : null}

        {stackToCards && isMobile
          ? table.getRowModel().rows.map((row) => (
              <li
                key={`card-${row.id}`}
                className={[
                  "rm-table-body",
                  "rm-table__body",
                  typeof getRowClassName === "function"
                    ? getRowClassName(row.original) || ""
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div className="data-card">
                  <section className="container">
                    <header>
                      <div className="headerBody">
                        <h3 className="headerTitle">
                          {String(
                            row.original[visibleDataColumns[0]?.id] ??
                              `Row ${row.index + 1}`,
                          )}
                        </h3>
                        {visibleDataColumns[1] && (
                          <h4 className="headerSubtitle">
                            {String(
                              row.original[visibleDataColumns[1].id] ?? "",
                            )}
                          </h4>
                        )}
                      </div>
                    </header>
                    <div className="cardBody">
                      {visibleDataColumns.slice(2).map((column) => (
                        <div
                          key={column.id}
                          className="row gap-sm"
                          aria-label={column.columnDef.header}
                        >
                          <strong style={{ minWidth: "96px" }}>
                            {String(column.columnDef.header)}:
                          </strong>
                          <span>{String(row.original[column.id] ?? "")}</span>
                        </div>
                      ))}
                      {typeof renderRowActions === "function" && (
                        <div className="rm-table__row-actions rm-table__row-actions--mobile">
                          {renderRowActions(row.original)}
                        </div>
                      )}
                    </div>
                  </section>
                </div>
              </li>
            ))
          : table.getRowModel().rows.map((row) => (
              <li
                key={row.id}
                className={[
                  "rm-table-body",
                  "rm-table__body",
                  "row",
                  "grid",
                  "grid-lead-fixed",
                  typeof getRowClassName === "function"
                    ? getRowClassName(row.original) || ""
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {row.getVisibleCells().map((cell) => (
                  <CellView
                    key={cell.id}
                    id={`cell-${row.id}-${cell.column.id}`}
                    roleType="td"
                    className={[
                      isDraggingCol && dragColKey.current === cell.column.id
                        ? "rm-col--dragging rm-col--fading"
                        : "",
                      isDraggingCol &&
                      dragOverKey === cell.column.id &&
                      dragColKey.current !== cell.column.id
                        ? "rm-col--placeholder"
                        : "",
                      cell.column.id === "__select"
                        ? "rm-td-checkbox rm-td--checkbox"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </CellView>
                ))}
              </li>
            ))}

        <li className="rm-table-foot rm-table__foot">
          <div className="rm-table-row rm-table__row">
            <CellView
              roleType="td"
              id="cell-foot-summary"
              style={{ gridColumn: "1 / -1" }}
            >
              {`Showing ${table.getRowModel().rows.length} entries`}
            </CellView>
          </div>
        </li>
      </ul>

      {editable && confirmDel && (
        <Modal
          open
          onClose={() => setConfirmDel(null)}
          title="Delete column?"
          footer={
            <>
              <Button
                className="primary"
                link
                error
                label="Cancel"
                onClick={() => setConfirmDel(null)}
              />
              <Button error label="Delete" onClick={handleConfirmDelete} />
            </>
          }
        >
          <p>Are you sure you want to delete “{confirmDel.label}”?</p>
        </Modal>
      )}
    </div>
  );
}
